import { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  LayoutDashboard,
  Barcode,
  CalendarDays,
  Globe,
  Package,
  FolderTree,
  Tag,
  Percent,
  Ruler,
  Warehouse,
  RefreshCw,
  Briefcase,
  Wallet,
  TrendingUp,
  Building2,
  Users,
  ShieldCheck,
  ScrollText,
  Bot,
  Building,
  LogOut,
  Languages,
  RotateCcw,
  Star,
  ArrowDownCircle,
  ArrowUpCircle,
  BookOpen,
  Settings,
  ShoppingCart,
  Ship,
  MapPinned,
  Menu,
  Wrench,
  X,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { useI18n, type Locale } from "../i18n/I18nContext";
import { useDemoFilter } from "../context/DemoFilterContext";
import { isPreviewEnvironment, previewEnvironmentLabel } from "../lib/preview";

type Role = "admin" | "rahbar" | "manager" | "buxgalter" | "sales_manager" | "warehouse" | "installer" | "finance" | "hr" | "user" | "ai" | "dealer" | "call_center" | "compliance";

interface NavItem {
  to: string;
  labelKey?: string;
  label?: string;
  icon?: LucideIcon;
  group?: "main" | "sales" | "branches" | "inventory" | "hr" | "finance" | "reports" | "admin";
  roles?: Role[];
}

const navItems: NavItem[] = [
  { to: "/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard, group: "main" },
  { to: "/ai", labelKey: "nav.ai", icon: Bot, group: "main", roles: ["admin", "rahbar", "manager", "buxgalter", "sales_manager", "warehouse", "finance", "hr", "dealer", "installer", "call_center", "compliance"] },
  { to: "/knowledge", labelKey: "nav.knowledge", icon: BookOpen, group: "main" },
  { to: "/crm", labelKey: "nav.crm", icon: Users, group: "sales" },
  { to: "/products", labelKey: "nav.products", icon: Package, group: "sales" },
  { to: "/categories", labelKey: "nav.categories", icon: FolderTree, group: "sales" },
  { to: "/brands", labelKey: "nav.brands", icon: Tag, group: "sales" },
  { to: "/units", labelKey: "nav.units", icon: Ruler, group: "sales" },
  { to: "/sales", labelKey: "nav.sales", icon: Briefcase, group: "sales" },
  { to: "/returns", labelKey: "nav.returns", icon: RotateCcw, group: "sales", roles: ["admin"] },
  { to: "/client-returns", labelKey: "nav.client_returns", icon: RotateCcw, group: "sales", roles: ["admin", "rahbar", "manager", "sales_manager"] },
  { to: "/services", labelKey: "nav.services", icon: Wrench, group: "sales", roles: ["admin", "rahbar", "manager"] },
  { to: "/price-types", labelKey: "nav.price_types", icon: Tag, group: "sales", roles: ["admin", "rahbar"] },
  { to: "/portal-orders", labelKey: "nav.portal_orders", icon: Globe, group: "sales", roles: ["admin", "rahbar", "manager", "sales_manager"] },
  { to: "/branches", labelKey: "nav.branches", icon: MapPinned, group: "branches", roles: ["admin", "rahbar"] },
  { to: "/filial-transfers", labelKey: "nav.filial_transfers", icon: RefreshCw, group: "branches", roles: ["admin", "rahbar", "warehouse"] },
  { to: "/warehouses", labelKey: "nav.warehouses", icon: Warehouse, group: "inventory" },
  { to: "/stock-movements", labelKey: "stock.title", icon: RefreshCw, group: "inventory" },
  { to: "/serial-variant-verification", labelKey: "nav.serial_verification", icon: Barcode, group: "inventory", roles: ["admin", "warehouse", "manager"] },
  { to: "/print-barcode", labelKey: "nav.print_barcode", icon: Barcode, group: "inventory", roles: ["admin", "warehouse", "manager"] },
  { to: "/hr", labelKey: "nav.hr_employees", icon: Users, group: "hr", roles: ["admin", "rahbar", "manager", "hr"] },
  { to: "/hr/departments", labelKey: "nav.hr_departments", icon: Building2, group: "hr", roles: ["admin", "rahbar", "hr"] },
  { to: "/hr/positions", labelKey: "nav.hr_positions", icon: Briefcase, group: "hr", roles: ["admin", "rahbar", "hr"] },
  { to: "/hr/payroll", labelKey: "nav.hr_payroll", icon: Wallet, group: "hr", roles: ["admin", "rahbar", "hr", "finance"] },
  { to: "/hr/kpi", labelKey: "nav.hr_kpi", icon: TrendingUp, group: "hr", roles: ["admin", "rahbar", "manager", "hr"] },
  { to: "/hr/attendance", labelKey: "nav.hr_attendance", icon: CalendarDays, group: "hr", roles: ["admin", "rahbar", "manager", "hr"] },
  { to: "/finance", labelKey: "nav.finance", icon: Wallet, group: "finance", roles: ["admin", "rahbar", "buxgalter", "finance"] },
  { to: "/accounting", label: "Бухгалтерия", icon: ScrollText, group: "finance", roles: ["admin", "rahbar", "buxgalter", "finance"] },
  { to: "/loans", label: "Кредитлар", icon: Wallet, group: "finance", roles: ["admin", "rahbar", "buxgalter", "finance"] },
  { to: "/payment-received", labelKey: "nav.payment_received", icon: ArrowDownCircle, group: "finance" },
  { to: "/payment-made", labelKey: "nav.payment_made", icon: ArrowUpCircle, group: "finance" },
  { to: "/suppliers-investors", labelKey: "finance.suppliers", icon: Building2, group: "finance" },
  { to: "/purchases", labelKey: "nav.purchases", icon: ShoppingCart, group: "finance" },
  { to: "/imports", labelKey: "nav.imports", icon: Ship, group: "finance", roles: ["admin", "rahbar", "warehouse", "finance"] },
  { to: "/points", labelKey: "nav.rewards", icon: Star, group: "finance", roles: ["admin", "rahbar"] },
  { to: "/reports", labelKey: "reports.title", icon: TrendingUp, group: "reports" },
  { to: "/daily-report", labelKey: "nav.daily_report", icon: CalendarDays, group: "reports", roles: ["admin", "rahbar"] },
  { to: "/users", labelKey: "nav.users", icon: Users, group: "admin", roles: ["admin"] },
  { to: "/roles", labelKey: "nav.roles", icon: ShieldCheck, group: "admin", roles: ["admin"] },
  { to: "/promotions", labelKey: "nav.promotions", icon: Percent, group: "admin", roles: ["admin"] },
  { to: "/audit", labelKey: "audit.title", icon: ScrollText, group: "admin", roles: ["admin"] },
  { to: "/settings", labelKey: "nav.settings", icon: Settings, group: "admin", roles: ["admin"] },
];

function userRolesFromContext(user: any): Role[] {
  if (!user) return [];
  const r = Array.isArray(user.roles) ? user.roles : [];
  if (user.is_superuser && !r.includes("admin")) r.push("admin");
  return r as Role[];
}

function canSee(item: NavItem, userRoles: Role[]): boolean {
  if (!item.roles || item.roles.length === 0) return true;
  return item.roles.some((r) => userRoles.includes(r));
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, locale, setLocale } = useI18n();
  const { showDemo, canUseDemoData, setShowDemo } = useDemoFilter();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem("wst.sidebar.collapsedGroups");
      return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  });
  const toggleGroup = (group: string) => {
    setCollapsedGroups((current) => {
      const next = { ...current, [group]: !current[group] };
      try { window.localStorage.setItem("wst.sidebar.collapsedGroups", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const userRoles = userRolesFromContext(user);
  const visibleItems = navItems.filter((it) => canSee(it, userRoles));
  const groupOrder: Array<"main" | "sales" | "branches" | "inventory" | "hr" | "finance" | "reports" | "admin"> = ["main", "sales", "branches", "inventory", "hr", "finance", "reports", "admin"];
  const grouped: Record<string, NavItem[]> = {};
  for (const it of visibleItems) {
    const g = it.group || "main";
    if (!grouped[g]) grouped[g] = [];
    grouped[g].push(it);
  }

  return (
    <div className="app-shell">
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} aria-hidden="true" />}
      <aside className={`app-sidebar ${sidebarOpen ? "open" : ""}`} aria-label={t("nav.sidebar") || "Asosiy navigatsiya"}>
        <div className="brand">
          <img src="/logo-mark.png" alt="World Security Technology" style={{ height: 26, width: 26, objectFit: "contain", marginRight: 8 }} />
          <span>WST ERP AI Pilot</span>
        </div>
        <nav role="navigation" aria-label={t("nav.main") || "Asosiy menyu"}>
          {groupOrder.map((g) => {
            const items = grouped[g];
            if (!items || items.length === 0) return null;
            const isCollapsed = collapsedGroups[g] === true;
            return (
              <div key={g} className={`nav-group ${isCollapsed ? "nav-group-collapsed" : ""}`.trim()}>
                <button type="button" className="nav-group-title" aria-expanded={!isCollapsed} aria-controls={`nav-group-${g}`} onClick={() => toggleGroup(g)}>
                  <span className="nav-group-caret" aria-hidden="true">{isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}</span>
                  <span className="nav-group-label">{g === "finance" ? "БУХГАЛТЕРИЯ / МОЛИЯ" : t(`nav.group.${g}`)}</span>
                </button>
                {!isCollapsed && (
                  <div id={`nav-group-${g}`} className="nav-group-items">
                    {items.map((item) => (
                      <NavLink key={item.to} to={item.to} className={({ isActive }) => (isActive ? "active" : "")}>
                        {item.icon && <span className="nav-icon"><item.icon size={18} strokeWidth={2} aria-hidden="true" /></span>}
                        <span className="nav-label">{item.label || (item.labelKey ? t(item.labelKey) : "")}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>
      <header className="app-topbar">
        <button className="hamburger-btn" onClick={() => setSidebarOpen((v) => !v)} aria-label="Менюни очиш/ёпиш" aria-expanded={sidebarOpen}>
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <div className="who">{user ? t("auth.logged_in_as", { name: user.full_name || user.username }) : ""}</div>
        <div className="flex">
          {canUseDemoData && (
            <label className="demo-toggle" title={t("demo_filter.show")} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", border: "1px solid #d6dadf", borderRadius: 4, cursor: "pointer", fontSize: 13, background: showDemo ? "#fef3c7" : "#f7f9fb" }}>
              <input type="checkbox" checked={showDemo} onChange={(e) => setShowDemo(e.target.checked)} style={{ margin: 0 }} />
              {t("demo_filter.show")}
            </label>
          )}
          <span className="locale-wrap" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Languages size={14} aria-hidden="true" />
            <select data-no-translit value={locale} onChange={(e) => setLocale(e.target.value as Locale)} style={{ padding: "4px 8px", border: "1px solid #d6dadf", borderRadius: 4 }} aria-label={t("nav.language") || "Til"}>
              <option value="uz-Cyrl">O'zbek (kirill)</option><option value="uz-Latn">O'zbek (lotin)</option><option value="ru">Русский</option><option value="en">English</option>
            </select>
          </span>
          {user && <Link to="/change-password" className="topbar-password-link" style={{ padding: "4px 8px", border: "1px solid #d6dadf", borderRadius: 4, color: "#1a73e8", textDecoration: "none", fontSize: 14 }}>{t("auth.change_password")}</Link>}
          <button className="logout" onClick={logout} aria-label={t("auth.logout") || "Тизимдан чиқиш"} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><LogOut size={14} aria-hidden="true" />{t("auth.logout")}</button>
        </div>
      </header>
      <main className="app-content" role="main" id="main-content">
        {isPreviewEnvironment && <div className="preview-environment-banner" role="status" aria-live="polite">{previewEnvironmentLabel}</div>}
        <Outlet />
      </main>
    </div>
  );
}
